import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { PageContainer } from '@/components/layout/PageContainer';
import {
  Settings as SettingsIcon,
  Bell,
  Palette,
  Globe,
  LogOut,
  Moon,
  Sun,
  Check,
} from 'lucide-react';
import { useColorMode } from '@/contexts/ColorModeContext';
import { useAuth } from '@/contexts/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

// ─── Types ────────────────────────────────────────────────────────────────────

interface NotificationSettings {
  emailNotifications: boolean;
  smsNotifications: boolean;
  appointmentReminders: boolean;
  testResultAlerts: boolean;
  marketingEmails: boolean;
}

interface AppSettings {
  language: 'en' | 'fr';
  notifications: NotificationSettings;
}

// ─── Storage helpers ──────────────────────────────────────────────────────────

const STORAGE_KEY = 'kaimed_app_settings';

const defaultSettings: AppSettings = {
  language: 'en',
  notifications: {
    emailNotifications: true,
    smsNotifications: false,
    appointmentReminders: true,
    testResultAlerts: true,
    marketingEmails: false,
  },
};

function loadSettings(): AppSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultSettings;
    return { ...defaultSettings, ...JSON.parse(raw) };
  } catch {
    return defaultSettings;
  }
}

function saveSettings(settings: AppSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // localStorage may be unavailable in some environments — safe to ignore
  }
}

// ─── Toggle switch ─────────────────────────────────────────────────────────────

interface ToggleSwitchProps {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  description?: string;
}

const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ id, checked, onChange, label, description }) => (
  <div className="flex items-center justify-between py-3 border-b border-[var(--background-inset)] last:border-0">
    <div className="flex-1 mr-4">
      <label htmlFor={id} className="text-sm font-medium text-[var(--text-primary)] cursor-pointer">
        {label}
      </label>
      {description && (
        <p className="text-xs text-[var(--text-tertiary)] mt-0.5">{description}</p>
      )}
    </div>
    <button
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className={`
        relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2
        focus-visible:ring-[var(--brand-primary)] focus-visible:ring-offset-2
        ${checked ? 'bg-[var(--brand-primary)]' : 'bg-[var(--background-inset)]'}
      `}
    >
      <span className="sr-only">Toggle {label}</span>
      <span
        aria-hidden="true"
        className={`
          pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md
          ring-0 transition duration-200 ease-in-out
          ${checked ? 'translate-x-5' : 'translate-x-0'}
        `}
      />
    </button>
  </div>
);

// ─── Section card ──────────────────────────────────────────────────────────────

interface SettingsSectionProps {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ title, icon, children }) => (
  <Card>
    <CardHeader>
      <CardTitle className="flex items-center gap-2 text-base">
        <span className="text-[var(--brand-primary)]">{icon}</span>
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent>{children}</CardContent>
  </Card>
);

// ─── Main component ────────────────────────────────────────────────────────────

const Settings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { colorMode, toggleColorMode } = useColorMode();
  const { signOut } = useAuth();

  const [settings, setSettings] = useState<AppSettings>(loadSettings);
  const [savedIndicator, setSavedIndicator] = useState(false);

  // Persist to localStorage whenever settings change
  useEffect(() => {
    saveSettings(settings);
  }, [settings]);

  // Sync i18n language when the language setting changes
  useEffect(() => {
    if (i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language);
    }
  }, [settings.language, i18n]);

  const updateNotification = useCallback(
    (key: keyof NotificationSettings, value: boolean) => {
      setSettings(prev => ({
        ...prev,
        notifications: { ...prev.notifications, [key]: value },
      }));
      // Brief "saved" indicator
      setSavedIndicator(true);
      setTimeout(() => setSavedIndicator(false), 1500);
    },
    []
  );

  const updateLanguage = useCallback((lang: 'en' | 'fr') => {
    setSettings(prev => ({ ...prev, language: lang }));
    setSavedIndicator(true);
    setTimeout(() => setSavedIndicator(false), 1500);
  }, []);

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <PageContainer
      title={t('settings.pageTitle', 'Settings')}
      description={t(
        'settings.pageDescription',
        'Manage your appearance, notifications, language, and account.'
      )}
      icon={<SettingsIcon />}
      actions={
        savedIndicator ? (
          <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
            <Check className="w-4 h-4" />
            Saved
          </span>
        ) : undefined
      }
    >
      <div className="space-y-6 max-w-2xl">
        {/* Appearance */}
        <SettingsSection title="Appearance" icon={<Palette className="w-4 h-4" />}>
          <div className="flex items-center justify-between py-3">
            <div className="flex-1 mr-4">
              <p className="text-sm font-medium text-[var(--text-primary)]">Theme</p>
              <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                Choose between light and dark mode
              </p>
            </div>
            <button
              onClick={toggleColorMode}
              aria-label={`Switch to ${colorMode === 'light' ? 'dark' : 'light'} mode`}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-[var(--background-inset)]
                bg-[var(--background-surface)] text-[var(--text-primary)] text-sm font-medium
                hover:bg-[var(--background-inset)] transition-colors focus-visible:outline-none
                focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]"
            >
              {colorMode === 'light' ? (
                <>
                  <Moon className="w-4 h-4" />
                  Dark mode
                </>
              ) : (
                <>
                  <Sun className="w-4 h-4" />
                  Light mode
                </>
              )}
            </button>
          </div>
        </SettingsSection>

        {/* Notifications */}
        <SettingsSection title="Notifications" icon={<Bell className="w-4 h-4" />}>
          <ToggleSwitch
            id="emailNotifications"
            checked={settings.notifications.emailNotifications}
            onChange={v => updateNotification('emailNotifications', v)}
            label="Email Notifications"
            description="Receive account and health updates by email"
          />
          <ToggleSwitch
            id="smsNotifications"
            checked={settings.notifications.smsNotifications}
            onChange={v => updateNotification('smsNotifications', v)}
            label="SMS Notifications"
            description="Receive text message alerts on your mobile phone"
          />
          <ToggleSwitch
            id="appointmentReminders"
            checked={settings.notifications.appointmentReminders}
            onChange={v => updateNotification('appointmentReminders', v)}
            label="Appointment Reminders"
            description="Get reminded 24 hours before upcoming appointments"
          />
          <ToggleSwitch
            id="testResultAlerts"
            checked={settings.notifications.testResultAlerts}
            onChange={v => updateNotification('testResultAlerts', v)}
            label="Test Result Alerts"
            description="Be notified when new test results are available"
          />
          <ToggleSwitch
            id="marketingEmails"
            checked={settings.notifications.marketingEmails}
            onChange={v => updateNotification('marketingEmails', v)}
            label="Health Tips & News"
            description="Occasional health tips and updates from KaiMed"
          />
        </SettingsSection>

        {/* Language */}
        <SettingsSection title="Language" icon={<Globe className="w-4 h-4" />}>
          <div className="flex items-center gap-3 py-2">
            <p className="text-sm font-medium text-[var(--text-primary)] mr-4">
              Display language
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => updateLanguage('en')}
                aria-pressed={settings.language === 'en'}
                className={`
                  px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]
                  ${
                    settings.language === 'en'
                      ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                      : 'bg-[var(--background-surface)] text-[var(--text-primary)] border-[var(--background-inset)] hover:bg-[var(--background-inset)]'
                  }
                `}
              >
                EN
              </button>
              <button
                onClick={() => updateLanguage('fr')}
                aria-pressed={settings.language === 'fr'}
                className={`
                  px-4 py-1.5 rounded-lg text-sm font-medium border transition-colors
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)]
                  ${
                    settings.language === 'fr'
                      ? 'bg-[var(--brand-primary)] text-white border-[var(--brand-primary)]'
                      : 'bg-[var(--background-surface)] text-[var(--text-primary)] border-[var(--background-inset)] hover:bg-[var(--background-inset)]'
                  }
                `}
              >
                FR
              </button>
            </div>
          </div>
        </SettingsSection>

        {/* Account */}
        <SettingsSection title="Account" icon={<LogOut className="w-4 h-4" />}>
          <div className="flex items-center justify-between py-3">
            <div className="flex-1 mr-4">
              <p className="text-sm font-medium text-[var(--text-primary)]">Sign Out</p>
              <p className="text-xs text-[var(--text-tertiary)] mt-0.5">
                End your current session and return to the login screen
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={handleSignOut}
              className="flex items-center gap-2"
            >
              <LogOut className="w-4 h-4" />
              Sign Out
            </Button>
          </div>
        </SettingsSection>
      </div>
    </PageContainer>
  );
};

export default Settings;
